import {
  serializeReportExplorerInitialSelection,
  type ReportExplorerRouteSearch,
} from '../reporting/reportExplorer/reportExplorerRouteSearch';
import {
  ReportDateRangeType,
  ReportDimension,
  ReportMetric,
} from '../reporting/reportExplorer/reportEnums';
import {
  DashboardDatePreset,
  DashboardMetric,
  DashboardWidgetType,
  type DashboardDateRange,
  type DashboardWidget,
} from './model';

const reportDatePresetByDashboardPreset: Record<DashboardDatePreset, ReportDateRangeType> = {
  [DashboardDatePreset.Today]: ReportDateRangeType.Today,
  [DashboardDatePreset.Yesterday]: ReportDateRangeType.Yesterday,
  [DashboardDatePreset.ThisWeek]: ReportDateRangeType.ThisWeek,
  [DashboardDatePreset.Last7Days]: ReportDateRangeType.Last7Days,
  [DashboardDatePreset.ThisMonth]: ReportDateRangeType.ThisMonth,
  [DashboardDatePreset.Last30Days]: ReportDateRangeType.Last30Days,
  [DashboardDatePreset.LastMonth]: ReportDateRangeType.LastMonth,
  [DashboardDatePreset.Last3Months]: ReportDateRangeType.Last3Months,
};

export function dashboardReportExplorerSearch(
  widget: DashboardWidget,
  range: DashboardDateRange,
): ReportExplorerRouteSearch | null {
  const selection = dashboardReportSelection(widget, range);
  if (!selection) return null;
  return serializeReportExplorerInitialSelection({
    ...selection,
    range: {
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
      ...reportDatePreset(range),
    },
  });
}

function dashboardReportSelection(widget: DashboardWidget, range: DashboardDateRange) {
  switch (widget.type) {
    case DashboardWidgetType.PerformanceChart:
      return {
        dimensions: [range.dateFrom === range.dateTo ? ReportDimension.Hour : ReportDimension.Day],
        metrics: widget.metrics.includes(DashboardMetric.SuperAdminProfit)
          ? [
              ReportMetric.AdRequests,
              ReportMetric.AdImpressions,
              ReportMetric.Profit,
              ReportMetric.Revenue,
              ReportMetric.AdRequestsFilled,
              ReportMetric.Rpm,
              ReportMetric.NetworkRevenue,
            ]
          : [
              ReportMetric.AdRequests,
              ReportMetric.AdImpressions,
              ReportMetric.NetworkProfit,
              ReportMetric.Revenue,
              ReportMetric.AdRequestsFilled,
              ReportMetric.Rpm,
              ReportMetric.PubRevenue,
            ],
      };
    case DashboardWidgetType.PublisherPerformance:
      return {
        dimensions: [ReportDimension.Publisher],
        metrics: [ReportMetric.AdRequests, ReportMetric.AdImpressions, ReportMetric.PubRevenue],
      };
    case DashboardWidgetType.DemandIntegrations:
      return {
        dimensions: [ReportDimension.DemandIntegration],
        metrics: [
          ReportMetric.AdRequests,
          ReportMetric.Bids,
          ReportMetric.BidRatio,
          ReportMetric.WinRatio,
          ReportMetric.Revenue,
          ReportMetric.AdRequestsFilled,
        ],
      };
    case DashboardWidgetType.SrpmRanking:
      return {
        dimensions: [ReportDimension.DemandIntegration],
        metrics: [ReportMetric.DpAdRequests, ReportMetric.Srpm, ReportMetric.Revenue],
      };
    case DashboardWidgetType.DemandPartners:
      return {
        dimensions: [ReportDimension.DemandPartner],
        metrics: [
          ReportMetric.DpAdRequests,
          ReportMetric.Bids,
          ReportMetric.BidRatio,
          ReportMetric.WinRatio,
          ReportMetric.AdRequestsFilled,
          ReportMetric.WonBids,
          ReportMetric.Revenue,
          ReportMetric.NetworkMarginPct,
        ],
      };
    case DashboardWidgetType.NetworkPerformance:
      return {
        dimensions: [ReportDimension.Network],
        metrics: [
          ReportMetric.AdRequests,
          ReportMetric.AdImpressions,
          ReportMetric.NetworkRevenue,
          ReportMetric.NetworkProfit,
        ],
      };
    case DashboardWidgetType.Metric:
      return null;
  }
}

function reportDatePreset(range: DashboardDateRange) {
  const preset = range.preset ? reportDatePresetByDashboardPreset[range.preset] : undefined;
  return preset ? { preset } : {};
}
